import { apiClient } from './api'
import { formatMoney } from '@/utils/formatMoney'

// Enums matching backend
export enum SubscriptionStatus {
  Trial = 0,
  Active = 1,
  PastDue = 2,
  Cancelled = 3,
  Expired = 4,
  Paused = 5,
  Free = 6,
  Trialing = 7,
  PendingPayment = 8,
  Cancelling = 9
}

export enum BillingCycle {
  Monthly = 0,
  Yearly = 1
}

export enum InvoiceStatus {
  Draft = 0,
  Open = 1,
  Paid = 2,
  Void = 3,
  Uncollectible = 4,
  Refunded = 5
}

// DTOs
export interface SubscriptionPlanDto {
  id: string
  name: string
  description: string
  monthlyPrice: number
  yearlyPrice: number
  currency: string
  maxServices: number
  maxLocationsPerService: number
  maxAppointmentsPerMonth: number
  commissionRate: number
  features: string[]
  isActive: boolean
  isFree: boolean
  sortOrder: number
  stripePriceIdMonthly?: string
  stripePriceIdYearly?: string
  stripeProductId?: string
  subscriberCount?: number
  createdAt: string
  updatedAt?: string
}

export interface ProviderSubscriptionDto {
  id: string
  providerId: string
  subscriptionPlanId: string
  planName: string
  status: SubscriptionStatus
  statusDisplay: string
  billingCycle: BillingCycle
  billingCycleDisplay: string
  currentPeriodStart: string
  currentPeriodEnd: string
  cancelAtPeriodEnd: boolean
  cancelAt?: string
  cancelledAt?: string
  trialEndDate?: string
  autoRenew: boolean
  monthlyPrice: number
  yearlyPrice: number
  currentPrice: number
  currency: string
  maxServices: number
  maxLocationsPerService: number
  maxAppointmentsPerMonth: number
  commissionRate: number
  features: string[]
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  createdAt: string
  updatedAt?: string
}

export interface SubscriptionInvoiceDto {
  id: string
  providerId: string
  providerSubscriptionId: string
  invoiceNumber: string
  status: InvoiceStatus
  statusDisplay: string
  periodStart: string
  periodEnd: string
  planName: string
  billingCycle: BillingCycle
  subtotal: number
  taxAmount: number
  discountAmount: number
  totalAmount: number
  currency: string
  paidAt?: string
  dueDate?: string
  paymentMethodDescription?: string
  stripeInvoiceUrl?: string
  stripePdfUrl?: string
  refundedAmount?: number
  refundedAt?: string
  refundReason?: string
  createdAt: string
}

export interface AdminSubscriptionInvoiceDto extends SubscriptionInvoiceDto {
  providerName: string
  stripeInvoiceId?: string
  stripePaymentIntentId?: string
  failureReason?: string
  retryCount: number
  notes?: string
}

export interface UsageSummaryDto {
  planName: string
  isFree: boolean
  // Services
  serviceCount: number
  serviceLimit: number | null
  serviceUsagePercent: number
  serviceLimitReached: boolean
  // Locations
  locationCount: number
  locationLimit: number | null
  locationUsagePercent: number
  locationLimitReached: boolean
  // Appointments
  appointmentCount: number
  appointmentLimit: number | null
  appointmentUsagePercent: number
  appointmentLimitReached: boolean
  // Commission
  commissionRate: number
  commissionDisplay: string
  // Status
  status: string
  canAddService: boolean
  canAddLocation: boolean
  canBookAppointment: boolean
  // Upgrade suggestion
  shouldSuggestUpgrade: boolean
  upgradeSuggestionReason?: string
}

export interface SubscriptionStatsDto {
  totalSubscribers: number
  activeSubscribers: number
  trialSubscribers: number
  freeSubscribers: number
  pastDueSubscribers: number
  cancelledThisMonth: number
  newSubscribersThisMonth: number
  monthlyRecurringRevenue: number
  subscribersByPlan?: { planId: string; planName: string; subscriberCount: number; revenue: number }[]
}

export interface InvoiceListResponse {
  invoices: SubscriptionInvoiceDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

export interface AdminInvoiceListResponse {
  invoices: AdminSubscriptionInvoiceDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  totalRevenue: number
}

// Request DTOs
export interface CreateSubscriptionPlanRequest {
  name: string
  description: string
  monthlyPrice: number
  yearlyPrice: number
  currency?: string
  maxServices: number
  maxLocationsPerService: number
  maxAppointmentsPerMonth: number
  commissionRate: number
  features: string[]
  isActive?: boolean
  isFree?: boolean
  sortOrder?: number
}

export interface UpdateSubscriptionPlanRequest extends CreateSubscriptionPlanRequest {
  stripePriceIdMonthly?: string
  stripePriceIdYearly?: string
  stripeProductId?: string
}

export interface SubscribeRequest {
  planId: string
  billingCycle: BillingCycle
  successUrl: string
  cancelUrl: string
}

export interface ChangePlanRequest {
  newPlanId: string
  prorationBehavior?: string
  billingCycle?: BillingCycle
  successUrl?: string
  cancelUrl?: string
}

export interface ChangePlanResponse {
  success: boolean
  message?: string
  subscription?: ProviderSubscriptionDto
  checkoutUrl?: string
  checkoutSessionId?: string
}

export interface ChangeBillingCycleRequest {
  newBillingCycle: BillingCycle
}

export interface CreatePortalSessionRequest {
  returnUrl: string
}

// Subscription Service
export const SubscriptionService = {
  // ==================== Admin Endpoints ====================

  // Get all subscription plans (admin)
  async getAdminPlans(): Promise<SubscriptionPlanDto[]> {
    return await apiClient.get<SubscriptionPlanDto[]>('/admin/subscription-plans')
  },

  // Get single plan by ID (admin)
  async getAdminPlanById(planId: string): Promise<SubscriptionPlanDto> {
    return await apiClient.get<SubscriptionPlanDto>(`/admin/subscription-plans/${planId}`)
  },

  // Create subscription plan (admin)
  async createPlan(request: CreateSubscriptionPlanRequest): Promise<SubscriptionPlanDto> {
    return await apiClient.post<SubscriptionPlanDto>('/admin/subscription-plans', request)
  },

  // Update subscription plan (admin)
  async updatePlan(planId: string, request: UpdateSubscriptionPlanRequest): Promise<SubscriptionPlanDto> {
    return await apiClient.put<SubscriptionPlanDto>(`/admin/subscription-plans/${planId}`, request)
  },

  // Set plan status (admin)
  async setPlanStatus(planId: string, isActive: boolean): Promise<void> {
    return await apiClient.patch(`/admin/subscription-plans/${planId}/status`, { isActive })
  },

  // Sync plan with Stripe (admin)
  async syncPlanWithStripe(planId: string): Promise<SubscriptionPlanDto> {
    return await apiClient.post<SubscriptionPlanDto>(`/admin/subscription-plans/${planId}/sync-stripe`)
  },

  // Get subscription stats (admin)
  async getSubscriptionStats(): Promise<SubscriptionStatsDto> {
    return await apiClient.get<SubscriptionStatsDto>('/admin/subscription-plans/stats')
  },

  // Get all invoices (admin)
  async getAdminInvoices(page: number = 1, pageSize: number = 20, status?: InvoiceStatus): Promise<AdminInvoiceListResponse> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('pageSize', pageSize.toString())
    if (status !== undefined) {
      params.append('status', status.toString())
    }
    return await apiClient.get<AdminInvoiceListResponse>(`/admin/subscription-plans/invoices?${params.toString()}`)
  },

  // ==================== Provider Endpoints ====================

  // Get active plans (provider/public)
  async getActivePlans(): Promise<SubscriptionPlanDto[]> {
    return await apiClient.get<SubscriptionPlanDto[]>('/provider/subscription/plans')
  },

  // Get plan details (provider)
  async getPlanById(planId: string): Promise<SubscriptionPlanDto> {
    return await apiClient.get<SubscriptionPlanDto>(`/provider/subscription/plans/${planId}`)
  },

  // Get current subscription (provider)
  async getCurrentSubscription(): Promise<ProviderSubscriptionDto> {
    return await apiClient.get<ProviderSubscriptionDto>('/provider/subscription/current')
  },

  // Get usage summary (provider)
  async getUsageSummary(): Promise<UsageSummaryDto> {
    return await apiClient.get<UsageSummaryDto>('/provider/subscription/usage')
  },

  // Subscribe to plan (provider)
  async subscribe(request: SubscribeRequest): Promise<{ checkoutUrl: string }> {
    return await apiClient.post<{ checkoutUrl: string }>('/provider/subscription/subscribe', request)
  },

  // Change plan (provider) - may return checkoutUrl for Free->Paid upgrade
  async changePlan(request: ChangePlanRequest): Promise<ChangePlanResponse> {
    const response = await apiClient.post<ChangePlanResponse>('/provider/subscription/change-plan', request)
    if (!response.success) {
      throw new Error(response.message || 'Failed to change plan')
    }
    return response
  },

  // Change billing cycle (provider)
  async changeBillingCycle(request: ChangeBillingCycleRequest): Promise<ProviderSubscriptionDto> {
    const response = await apiClient.post<{ success: boolean; message?: string; subscription?: ProviderSubscriptionDto }>('/provider/subscription/change-billing-cycle', request)
    if (!response.subscription) {
      throw new Error(response.message || 'Failed to change billing cycle')
    }
    return response.subscription
  },

  // Cancel subscription (provider)
  async cancelSubscription(cancelImmediately: boolean = false): Promise<ProviderSubscriptionDto> {
    const response = await apiClient.post<{ success: boolean; message?: string; subscription?: ProviderSubscriptionDto }>('/provider/subscription/cancel', { cancelImmediately })
    if (!response.subscription) {
      throw new Error(response.message || 'Failed to cancel subscription')
    }
    return response.subscription
  },

  // Reactivate subscription (provider)
  async reactivateSubscription(): Promise<ProviderSubscriptionDto> {
    const response = await apiClient.post<{ success: boolean; message?: string; subscription?: ProviderSubscriptionDto }>('/provider/subscription/reactivate')
    if (!response.subscription) {
      throw new Error(response.message || 'Failed to reactivate subscription')
    }
    return response.subscription
  },

  // Set auto-renew preference (provider)
  async setAutoRenew(autoRenew: boolean): Promise<ProviderSubscriptionDto> {
    const response = await apiClient.post<{ success: boolean; message?: string; subscription?: ProviderSubscriptionDto }>('/provider/subscription/set-auto-renew', { autoRenew })
    if (!response.subscription) {
      throw new Error(response.message || 'Failed to update auto-renew preference')
    }
    return response.subscription
  },

  // Create customer portal session (provider)
  async createPortalSession(request: CreatePortalSessionRequest): Promise<{ portalUrl: string }> {
    const response = await apiClient.post<{ url?: string; success: boolean; message?: string }>('/provider/subscription/customer-portal', request)
    if (!response.success || !response.url) {
      throw new Error(response.message || 'Unable to open billing portal. This feature is only available for paid subscriptions.')
    }
    return { portalUrl: response.url }
  },

  // Get provider invoices (provider)
  async getProviderInvoices(page: number = 1, pageSize: number = 20): Promise<InvoiceListResponse> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('pageSize', pageSize.toString())
    return await apiClient.get<InvoiceListResponse>(`/provider/subscription/invoices?${params.toString()}`)
  },

  // ==================== Helpers ====================

  // Get status display name
  getStatusDisplayName(status: SubscriptionStatus): string {
    const statusNames: Record<SubscriptionStatus, string> = {
      [SubscriptionStatus.Trial]: 'Trial',
      [SubscriptionStatus.Active]: 'Active',
      [SubscriptionStatus.PastDue]: 'Past Due',
      [SubscriptionStatus.Cancelled]: 'Cancelled',
      [SubscriptionStatus.Expired]: 'Expired',
      [SubscriptionStatus.Paused]: 'Paused',
      [SubscriptionStatus.Free]: 'Free',
      [SubscriptionStatus.Trialing]: 'Trialing',
      [SubscriptionStatus.PendingPayment]: 'Pending Payment',
      [SubscriptionStatus.Cancelling]: 'Cancelling'
    }
    return statusNames[status] || 'Unknown'
  },

  // Get status badge class
  getStatusBadgeClass(status: SubscriptionStatus): string {
    const baseClass = 'text-xs px-2 py-1 rounded-full font-medium '
    switch (status) {
      case SubscriptionStatus.Active:
      case SubscriptionStatus.Free:
        return baseClass + 'bg-green-100 text-green-800'
      case SubscriptionStatus.Trial:
      case SubscriptionStatus.Trialing:
        return baseClass + 'bg-blue-100 text-blue-800'
      case SubscriptionStatus.PastDue:
      case SubscriptionStatus.PendingPayment:
        return baseClass + 'bg-yellow-100 text-yellow-800'
      case SubscriptionStatus.Cancelled:
      case SubscriptionStatus.Cancelling:
      case SubscriptionStatus.Expired:
        return baseClass + 'bg-red-100 text-red-800'
      case SubscriptionStatus.Paused:
        return baseClass + 'bg-gray-100 text-gray-800'
      default:
        return baseClass + 'bg-gray-100 text-gray-800'
    }
  },

  // Get invoice status badge class
  getInvoiceStatusBadgeClass(status: InvoiceStatus): string {
    const baseClass = 'text-xs px-2 py-1 rounded-full font-medium '
    switch (status) {
      case InvoiceStatus.Paid:
        return baseClass + 'bg-green-100 text-green-800'
      case InvoiceStatus.Open:
      case InvoiceStatus.Draft:
        return baseClass + 'bg-blue-100 text-blue-800'
      case InvoiceStatus.Void:
      case InvoiceStatus.Uncollectible:
        return baseClass + 'bg-red-100 text-red-800'
      case InvoiceStatus.Refunded:
        return baseClass + 'bg-purple-100 text-purple-800'
      default:
        return baseClass + 'bg-gray-100 text-gray-800'
    }
  },

  // Get billing cycle display name
  getBillingCycleDisplayName(cycle: BillingCycle): string {
    return cycle === BillingCycle.Monthly ? 'Monthly' : 'Yearly'
  },

  // Format currency - uses currency code (e.g., "GBP 10.00") instead of symbol
  formatCurrency(amount: number, currency: string = 'GBP'): string {
    return formatMoney(amount, currency)
  }
}

export default SubscriptionService
